// HKDSE 2016 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2016Samples = {
  algebra: [
    {
      id: 'hkdse2016_algebra_01',
      problem: "Simplify $\\frac{(x^8 y^7)^2}{x^5 y^{-6}}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_numerator",
            name: "Step 1: Simplify Numerator",
            type: "steps",
            description: "Apply power of power rule to (x⁸y⁷)²",
            value: "(x⁸y⁷)² = x¹⁶y¹⁴",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_divide",
            name: "Step 2: Divide by Denominator",
            type: "steps",
            description: "Divide x¹⁶y¹⁴ by x⁵y⁻⁶",
            value: "x¹⁶y¹⁴ ÷ (x⁵y⁻⁶) = x¹¹y²⁰",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "x¹¹y²⁰",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "power_of_power", to: "step1_simplify_numerator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_numerator", to: "step2_divide", type: "leads to", strength: 10 },
          { from: "step2_divide", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2016_algebra_02',
      problem: "Given that $x$ is the subject of the formula $Ax = (4x + B)C$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "bracket_expansion",
            name: "Bracket Expansion",
            type: "concepts",
            description: "Expanding brackets using distributive law",
            value: "a(b + c) = ab + ac",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "collecting_terms",
            name: "Collecting Terms",
            type: "concepts",
            description: "Gathering like terms on one side",
            value: "Group terms with same variable",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_expand_bracket",
            name: "Step 1: Expand Bracket",
            type: "steps",
            description: "Expand (4x + B)C",
            value: "(4x + B)C = 4xC + BC",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_collect_terms",
            name: "Step 2: Collect Terms",
            type: "steps",
            description: "Collect all terms with x on one side",
            value: "Ax - 4xC = BC",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_factor_x",
            name: "Step 3: Factor x",
            type: "steps",
            description: "Factor out x from left side",
            value: "x(A - 4C) = BC",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_solve_for_x",
            name: "Step 4: Solve for x",
            type: "steps",
            description: "Divide both sides by (A - 4C)",
            value: "x = BC/(A - 4C)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x expressed in terms of A, B, C",
            value: "x = BC/(A - 4C)",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "bracket_expansion", to: "step1_expand_bracket", type: "enables", strength: 10 },
          { from: "collecting_terms", to: "step2_collect_terms", type: "enables", strength: 9 },
          { from: "step1_expand_bracket", to: "step2_collect_terms", type: "leads to", strength: 9 },
          { from: "step2_collect_terms", to: "step3_factor_x", type: "leads to", strength: 10 },
          { from: "step3_factor_x", to: "step4_solve_for_x", type: "leads to", strength: 10 },
          { from: "step4_solve_for_x", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  algebra_fractions: [
    {
      id: 'hkdse2016_algebra_fractions_01',
      problem: "Simplify $\\frac{2}{4x-5} + \\frac{3}{1-6x}$.",
      problemType: "algebra_fractions",
      difficulty: "intermediate",
      description: "Adding algebraic fractions",
      graphData: {
        nodes: [
          {
            id: "common_denominator",
            name: "Common Denominator",
            type: "concepts",
            description: "Finding the least common multiple of denominators",
            value: "LCM of denominators",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method for adding fractions with different denominators",
            value: "a/b + c/d = (ad + bc)/bd",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_lcm",
            name: "Step 1: Find LCM",
            type: "steps",
            description: "Find LCM of (4x-5) and (1-6x)",
            value: "LCM = (4x-5)(1-6x)",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_cross_multiply",
            name: "Step 2: Cross Multiply",
            type: "steps",
            description: "Apply cross multiplication method",
            value: "2(1-6x) + 3(4x-5) / (4x-5)(1-6x)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_expand",
            name: "Step 3: Expand Numerator",
            type: "steps",
            description: "Expand the numerator",
            value: "(2-12x) + (12x-15) = -13",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified fraction",
            value: "-13/(4x-5)(1-6x)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "common_denominator", to: "step1_find_lcm", type: "enables", strength: 10 },
          { from: "cross_multiplication", to: "step2_cross_multiply", type: "enables", strength: 9 },
          { from: "step1_find_lcm", to: "step2_cross_multiply", type: "leads to", strength: 10 },
          { from: "step2_cross_multiply", to: "step3_expand", type: "leads to", strength: 9 },
          { from: "step3_expand", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2016_factorization_01',
      problem: "Factorize: (a) $5m - 10n$ (b) $m^2 + mn - 6n^2$ (c) $m^2 + mn - 6n^2 - 5m + 10n$",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of polynomial expressions",
      graphData: {
        nodes: [
          {
            id: "common_factor",
            name: "Common Factor",
            type: "concepts",
            description: "Finding the greatest common factor",
            value: "GCF of terms",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "quadratic_factorization",
            name: "Quadratic Factorization",
            type: "concepts",
            description: "Factoring quadratic expressions",
            value: "ax² + bx + c = (px + q)(rx + s)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_part_a",
            name: "Step 1: Part (a) - Common Factor",
            type: "steps",
            description: "Find GCF of 5m and -10n",
            value: "GCF = 5",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_part_a",
            name: "Step 2: Part (a) - Factor Out",
            type: "steps",
            description: "Factor out 5",
            value: "5(m - 2n)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_part_b",
            name: "Step 3: Part (b) - Quadratic",
            type: "steps",
            description: "Factor m² + mn - 6n²",
            value: "(m + 3n)(m - 2n)",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_part_c",
            name: "Step 4: Part (c) - Group Terms",
            type: "steps",
            description: "Group terms: (m² + mn - 6n²) + (-5m + 10n)",
            value: "(m + 3n)(m - 2n) + 5(-m + 2n)",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "step5_part_c",
            name: "Step 5: Part (c) - Factor Further",
            type: "steps",
            description: "Factor out common terms",
            value: "(m - 2n)(m + 3n - 5)",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Fully factorized expressions",
            value: "(a) 5(m - 2n), (b) (m + 3n)(m - 2n), (c) (m - 2n)(m + 3n - 5)",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "common_factor", to: "step1_part_a", type: "enables", strength: 10 },
          { from: "quadratic_factorization", to: "step3_part_b", type: "enables", strength: 9 },
          { from: "step1_part_a", to: "step2_part_a", type: "leads to", strength: 10 },
          { from: "step2_part_a", to: "step3_part_b", type: "leads to", strength: 8 },
          { from: "step3_part_b", to: "step4_part_c", type: "leads to", strength: 9 },
          { from: "step4_part_c", to: "step5_part_c", type: "leads to", strength: 10 },
          { from: "step5_part_c", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 